import { useState } from "react";
import { Link } from "react-router-dom";
import type { StoreEntryFlow, PublicIntakeProgram } from "@bloom/shared";
import { dtpPath } from "../../lib/clinicSite";

export function StoreEntryChoices({
  flow,
  programs,
  physicianId,
  children,
}: {
  flow?: StoreEntryFlow;
  programs: PublicIntakeProgram[];
  physicianId: string;
  children: React.ReactNode;
}) {
  const [selected, setSelected] = useState<"direct" | "guided">();
  if (!flow || flow.mode === "direct") return <>{children}</>;
  const available = programs.filter(
    (p) => p.active && flow.programIds.includes(p.id),
  );
  const mode = flow.mode === "guided" ? "guided" : selected;
  return (
    <>
      <section className="max-w-5xl mx-auto my-8 px-5 space-y-4">
        <h2 className="text-2xl font-semibold">
          How would you like to get started?
        </h2>
        {flow.introduction && (
          <p className="text-gray-600">{flow.introduction}</p>
        )}
        {flow.mode === "both" && (
          <div className="flex flex-wrap gap-3">
            <button
              className={`rounded-lg border px-5 py-3 ${mode === "direct" ? "bg-blue-600 text-white" : "bg-white"}`}
              aria-pressed={mode === "direct"}
              onClick={() => setSelected("direct")}
            >
              {flow.directLabel}
            </button>
            <button
              className={`rounded-lg border px-5 py-3 ${mode === "guided" ? "bg-blue-600 text-white" : "bg-white"}`}
              aria-pressed={mode === "guided"}
              onClick={() => setSelected("guided")}
            >
              {flow.guidedLabel}
            </button>
          </div>
        )}
        {mode === "guided" && (
          <div className="grid gap-4 sm:grid-cols-2">
            {available.map((p) => (
              <Link
                key={p.id}
                to={dtpPath(physicianId, `/intake/${p.id}`)}
                className="block rounded-xl border bg-white p-5 hover:border-blue-500"
              >
                <h3 className="font-semibold">{p.name}</h3>
                <p className="text-sm text-gray-600 mt-2">{p.description}</p>
                <span className="inline-block mt-4 text-blue-700">
                  Start assessment
                </span>
              </Link>
            ))}
            {!available.length && (
              <p>
                No guided programs are currently available. Contact your clinic.
              </p>
            )}
          </div>
        )}
      </section>
      {mode === "direct" && children}
    </>
  );
}
